// Editable site content.
//
// data.js holds the built-in defaults, so the site always works, even with no
// backend. The backend only stores *overrides* (what the admin panel changed).
// applyContent() mutates the data.js objects in place, so every component keeps
// reading the same exports; bumping `contentRev` tells the UI to re-render.
import { BASE } from "../api/base";
import { ZONES, certificates, journal, resumeFile, setResumeFile, site, works, workspaces } from "./data";
import { useStore } from "./store";

const clone = (v) => JSON.parse(JSON.stringify(v));
const DEFAULTS = {
  site: clone(site),
  works: clone(works),
  workspaces: clone(workspaces),
  certificates: clone(certificates),
  journal: clone(journal),
  resumeFile,
};
const CACHE_KEY = "sp-content";
let overrides = {};

export const getDefaults = () => clone(DEFAULTS);
export const getOverrides = () => overrides;

// Files uploaded through the admin panel live on the backend: /api/files/x.pdf
export const toAbs = (p) => (typeof p === "string" && p.startsWith("/api/") ? BASE + p : p);
/** Inverse of toAbs, for saving paths back to the backend in their stored form. */
export const toStored = (p) => (typeof p === "string" && BASE && p.startsWith(BASE + "/api/") ? p.slice(BASE.length) : p);
/** Cross-origin links ignore the `download` attribute, so ask the backend for an attachment instead. */
export const dl = (file) => (file && file.includes("/api/files/") ? `${file}${file.includes("?") ? "&" : "?"}download=1` : file);

function replaceObject(target, src) {
  Object.keys(target).forEach((k) => delete target[k]);
  Object.assign(target, clone(src));
}
function replaceArray(target, src) {
  target.splice(0, target.length, ...clone(src));
}

function refreshCaptions() {
  const set = (id, text) => {
    const z = ZONES.find((x) => x.id === id);
    if (z) z.caption = text;
  };
  const pieces = journal.reduce((n, g) => n + g.items.length, 0);
  set("hall", site.hero.sub);
  set("skills", `Tools and Environments, currently exploring ${site.exploring}.`);
  set("archive", `${certificates.length} certificate${certificates.length === 1 ? "" : "s"}. Preview inline, or download any of them directly.`);
  set("resume", `${site.role} · ${site.location}`);
  set("library", `Writing, on the side: ${pieces} pieces across poems, a monologue, and narration.`);
}

export function applyContent(content) {
  overrides = content && typeof content === "object" ? content : {};
  replaceObject(site, overrides.site || DEFAULTS.site);
  replaceArray(works, overrides.works || DEFAULTS.works);
  replaceArray(workspaces, overrides.workspaces || DEFAULTS.workspaces);
  replaceArray(certificates, overrides.certificates || DEFAULTS.certificates);
  certificates.forEach((c) => {
    c.file = toAbs(c.file);
  });
  replaceArray(journal, overrides.journal || DEFAULTS.journal);
  journal.forEach((g) => g.items.forEach((i) => {
    i.file = toAbs(i.file);
  }));
  setResumeFile(toAbs(overrides.resumeFile || DEFAULTS.resumeFile));
  refreshCaptions();
}

function saveCache(content, version) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ content, version }));
  } catch {
    /* storage blocked: the site still works, just without the instant-repeat-visit cache */
  }
}

/** Synchronous: show the last known content immediately on repeat visits. */
export function loadCachedContent() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (raw) applyContent(JSON.parse(raw).content);
  } catch {
    /* ignore a corrupt cache */
  }
}

/** Fetch the latest content in the background; re-render only if something changed. */
export async function syncContent() {
  if (!BASE) return;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 25000); // free hosts can take a while to wake up
  try {
    const res = await fetch(`${BASE}/api/content`, { signal: ctrl.signal, headers: { Accept: "application/json" } });
    if (!res.ok) return;
    const data = await res.json();
    useStore.getState().setAdminEnabled(Boolean(data.admin));
    const changed = JSON.stringify(data.content) !== JSON.stringify(overrides);
    saveCache(data.content, data.version);
    if (changed) {
      applyContent(data.content);
      useStore.getState().bumpContent();
    }
  } catch {
    /* offline or backend asleep: the cached/default content stays in place */
  } finally {
    clearTimeout(timer);
  }
}

/** After a successful admin save/reset: update the live site without a reload. */
export function commitOverride(key, value) {
  const next = { ...overrides };
  if (value === undefined) delete next[key];
  else next[key] = value;
  applyContent(next);
  saveCache(next, String(Date.now()));
  useStore.getState().bumpContent();
}

export function replaceAllOverrides(content) {
  applyContent(content);
  saveCache(content, String(Date.now()));
  useStore.getState().bumpContent();
}
